import React from "react"
import {
  Box, Divider, Heading, VStack, Text,
} from "@chakra-ui/react"
import ContainedForm from "components/ReactHookForm/ContainedForm"
import TextField from "components/ReactHookForm/TextField"
import * as Yup from "yup"
import TitleWithSubtitle from "pages/EventShow/Forms/TitleWithSubtitle"
import { GOOGLE_ANALYTICS_INTEGRATION_ARTICLE_LINK } from "utilities/externalLinks"
import Link from "./elements/Link"

export enum Variant {
  WebpageSetup,
  FormsGuestPortal,
}

type Props = {
  googleAnalyticsMeasurementId: string | null
  updateEvent: (googleAnalyticsMeasurementId: string) => Promise<void>
  variant: Variant
}

const googleAnalyticsSchema = Yup.object().shape({
  googleAnalyticsMeasurementId: Yup.string()
    .nullable()
    .matches(/^G-[A-Z0-9]{10}$/, {
      excludeEmptyString: true,
    }),
})

const GoogleAnalyticsForm = ({ googleAnalyticsMeasurementId, updateEvent, variant }: Props) => {
  const title = "Google Analytics"
  const subtitle = "Connect your Google Analytics account to track traffic and engagement across both the Event Web Page and the Guest Portal."
  const googleAnalyticsIntegrationHelp = (
    <Text mb={6}>To learn more, see {" "}
      <Link isExternal href={GOOGLE_ANALYTICS_INTEGRATION_ARTICLE_LINK}>
        Google Analytics Integration
      </Link>
      .
    </Text>
  )

  return (
    <Box>
      {variant === Variant.WebpageSetup
        ? (
          <>
            <Heading mb={2} fontSize="lg">{title}</Heading>
            <Text mb={4}>{subtitle}</Text>
            {googleAnalyticsIntegrationHelp}
          </>
        )
        : (
          <>
            <TitleWithSubtitle title={title} subtitle={subtitle} mb={4} />
            {googleAnalyticsIntegrationHelp}
          </>
        )}
      <ContainedForm
        values={{ googleAnalyticsMeasurementId }}
        onSubmit={updateEvent}
        validationSchema={googleAnalyticsSchema}
      >
        {({ control }) => (
          <VStack spacing={8} align="baseline">
            <TextField
              name="googleAnalyticsMeasurementId"
              control={control}
              label="Measurement ID"
              size="lg"
              placeholder="G-XXXXXXXXXX"
              helperText="Enter your Measurement ID (format: G-XXXXXXXXXX) to enable Google Analytics tracking."
              {...variant === Variant.FormsGuestPortal && { variant: "outline", vertical: true }}
            />
          </VStack>
        )}
      </ContainedForm>
      <Divider mt={6} />
    </Box>
  )
}

export default GoogleAnalyticsForm
